/* eslint-disable react-hooks/exhaustive-deps */
import { type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { TextInput } from '@/components/ui/form/fields/TextInput';
import { SelectInput, type SelectOption } from '@/components/ui/form/fields/SelectInput';
import { DateRangeInput } from '@/components/ui/form/fields/DateRangeInput';
import { type Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { roundedType } from 'pink-lava-ui/types';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Row, Col, Flex, Button as AntdButton, Divider } from 'antd';
import { debounce } from 'es-toolkit';
import type { SelectOptionsParams } from '@dc-trans/data-layer';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Modal } from 'pink-lava-ui';
import { SlidersHorizontal } from 'lucide-react';
import { SetFilterButton } from '@/components/ui/list/ListHeader';

type SearchOption = {
  name: string;
  label: string;
  placeholder?: string;
};

type BaseFilterField<T extends FieldValues> = {
  type: 'search' | 'select' | 'date-range';
  name: Path<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  allowSearch?: boolean;
  loading?: boolean;
  // For search fields: (value, fieldOnChange) => void
  // For select fields: (value, option, fieldOnChange) => void
  onChange?:
    | ((_value: any, _fieldOnChange: (_value: any) => void) => void)
    | ((_value: any, _option: any, _fieldOnChange: (_value: any) => void) => void);

  // For search fields: (value, setValue) => void
  // For select fields: (value, option, setValue) => void
  onChangeCallback?:
    | ((_value: any, _setValue: (_name: keyof T & string, _value: any) => void) => void)
    | ((_value: any, _option: any, _setValue: (_name: keyof T & string, _value: any) => void) => void);
  onSearch?: (_value: string) => void;
  validate?: (_value: any) => boolean | string;
  options?: ({ subLabel?: string } & SelectOption)[];
  searchOptions?: SearchOption[];
  size?: 'small' | 'middle' | 'large';
  className?: string;
  rounded?: roundedType;
  dependsOn?: Path<T>;
  disableIfEmpty?: boolean;
  hidden?: boolean;
  hiddenIfEmpty?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  mode?: 'multiple' | 'tags';
  trigger?: 'onChange' | 'onSubmit';
  onChangeDebounceMs?: number;
  onChangeLength?: number;
  cacheKey?: string;
  // For date-range fields
  minDate?: Dayjs | string | null;
  maxDate?: Dayjs | string | null;
};

// Conditional type: when apiUrl is defined, renderOption and apiParams are required
export type FilterFieldType<T extends FieldValues> = BaseFilterField<T> &
  (
    | {
        apiUrl: string;
        renderOption: (_item: any) => { subLabel?: string } & SelectOption;
        apiParams: SelectOptionsParams;
        searchKey: string;
        cacheKey?: string;
        allowClear?: boolean;
      }
    | {
        apiUrl?: never;
        renderOption?: never;
        apiParams?: never;
        searchKey?: never;
        allowClear?: never;
      }
  );

export type ColumnProps = {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
};

export const FilterBuilder = <T extends FieldValues>({
  fields,
  methods,
  layout = 'horizontal',
  showLabels = true,
  gutter = [16, 8],
  colProps,
  handleSubmit,
  hideFilterLabels,
}: {
  fields: FilterFieldType<T>[];
  methods: UseFormReturn<T>;
  layout?: 'horizontal' | 'vertical';
  showLabels?: boolean;
  gutter?: [number, number];
  colProps?: ColumnProps;
  handleSubmit: (_data: T) => void;
  handleReset: () => void;
  hideFilterLabels?: Path<T>[];
}) => {
  const { filters } = useUrlParamsContext();
  const { isMobile } = useBreakpoint();
  const [mobileFilterVisible, setMobileFilterVisible] = useState<boolean>(false);
  // Default responsive column props
  const defaultColProps = {
    xs: 24,
    sm: 12,
    md: 8,
    lg: 6,
    xl: 6,
    xxl: 4,
  };

  const colResponsive = colProps || defaultColProps;

  const searchFieldNames = fields[0]?.searchOptions?.map(item => item.name) || [fields[0]?.name || ''];

  const modalHasFilters =
    Object.keys(filters).filter(key => !searchFieldNames.includes(key) && !hideFilterLabels?.includes(key as Path<T>))
      .length > 0;

  if (isMobile) {
    return (
      <>
        <Flex className="flex items-end gap-2">
          <div className="flex-1">
            <FilterField field={fields[0]} methods={methods} showLabels={showLabels} />
          </div>
          <AntdButton
            className={`h-9! ${modalHasFilters ? 'text-pink-500! border-pink-500! bg-pink-100! shadow-none!' : ''}`}
            shape="round"
            onClick={() => setMobileFilterVisible(true)}
          >
            <Flex align="center" gap={8}>
              Filter
              <SlidersHorizontal size={16} />
            </Flex>
          </AntdButton>
        </Flex>
        {/* Hidden container to pre-render filter components */}
        <div style={{ display: 'none' }}>
          <Flex vertical align="center" gap={16}>
            {fields.slice(1).map(field => (
              <FilterField key={field.name} field={field} methods={methods} showLabels={true} />
            ))}
          </Flex>
        </div>
        <Modal
          centered
          title="Filter"
          open={mobileFilterVisible}
          onCancel={() => {
            setMobileFilterVisible(false);
            methods.reset({} as any);
          }}
          footer={
            <>
              <Divider className="my-4!" />
              <SetFilterButton
                handleSubmit={() => {
                  methods.handleSubmit(handleSubmit)();
                  setMobileFilterVisible(false);
                }}
              />
            </>
          }
        >
          <Flex vertical align="center" gap={16}>
            {fields.slice(1).map(field => (
              <FilterField key={field.name} field={field} methods={methods} showLabels={true} />
            ))}
          </Flex>
        </Modal>
      </>
    );
  }

  if (layout === 'vertical') {
    return (
      <div className="grid gap-4">
        {fields.map(field => (
          <div key={field.name}>
            <FilterField field={field} methods={methods} showLabels={showLabels} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <Row gutter={gutter} align="middle">
      {fields.map((field, index) => (
        <Col key={field.name || index} {...colResponsive}>
          <FilterField field={field} methods={methods} showLabels={showLabels} />
        </Col>
      ))}
    </Row>
  );
};

const FilterField = <T extends FieldValues>({
  field,
  methods,
  showLabels,
}: {
  field: FilterFieldType<T>;
  methods: UseFormReturn<T>;
  showLabels?: boolean;
}) => {
  const { updateUrlParams, removeParam, filters } = useUrlParamsContext();

  // Determine initial search option based on URL params
  const getInitialSearchOption = useCallback(() => {
    if (!field.searchOptions) return undefined;

    // Check if any search option has a corresponding URL parameter
    for (const option of field.searchOptions) {
      if (filters?.[option.name]) {
        return option;
      }
    }

    // Default to first option if no URL params match
    return field.searchOptions[0];
  }, [field.searchOptions, filters]);

  // State for selected search option
  const [selectedSearchOption, setSelectedSearchOption] = useState<SearchOption | undefined>(getInitialSearchOption());
  const [isInitialized, setIsInitialized] = useState(false);

  // Update selected search option when URL params change
  useEffect(() => {
    if (!isInitialized) {
      const newOption = getInitialSearchOption();
      if (newOption && newOption.name !== selectedSearchOption?.name) {
        setSelectedSearchOption(newOption);
      }
      setIsInitialized(true);
    }
  }, [field.searchOptions, isInitialized, selectedSearchOption?.name, getInitialSearchOption]);
  let isDisabled = field.disabled || false;

  if (field.dependsOn && field.disableIfEmpty) {
    const watchedValue = methods.watch(field.dependsOn as Path<T>);
    isDisabled = !watchedValue;
  }

  const currentFieldName = selectedSearchOption?.name || field.name;
  const currentValue: string = methods.watch(currentFieldName as Path<T>);

  const updateParamDebounceRef = useRef<ReturnType<typeof debounce> | undefined>(undefined);

  const updateParamCallback = useCallback(() => {
    const latestValue = methods.getValues()[currentFieldName];

    // Format date-range values if this is a date-range field
    let formattedValue = latestValue;
    if (field.type === 'date-range' && Array.isArray(latestValue)) {
      formattedValue = latestValue.map((dateValue: any) => {
        if (dateValue) {
          const date = dayjs(dateValue);
          return date.isValid() ? date.format('YYYY-MM-DD') : dateValue;
        }
        return ''; // Convert null/undefined to empty string to preserve array structure
      });
    }

    updateUrlParams({ [currentFieldName]: formattedValue, page: 1 });
  }, [currentFieldName, updateUrlParams, methods, field.type]);

  // Create or update the debounced function when dependencies change
  useMemo(() => {
    if (updateParamDebounceRef.current) {
      updateParamDebounceRef.current.cancel();
    }
    updateParamDebounceRef.current = debounce(updateParamCallback, field.onChangeDebounceMs || 500);
  }, [updateParamCallback, field.onChangeDebounceMs]);

  const updateParamDebounce = updateParamDebounceRef.current;

  useEffect(() => {
    if (field.trigger === 'onChange' && currentValue?.length >= (field.onChangeLength || 3) && updateParamDebounce) {
      updateParamDebounce();
    }
  }, [currentValue, field.trigger, field.onChangeLength]);

  // Handle search option change - clear all fields and URL params
  const handleSearchOptionChange = (option: SearchOption) => {
    if (field.searchOptions) {
      // Cancel any pending debounced updates to prevent re-adding old parameters
      if (updateParamDebounceRef.current) {
        updateParamDebounceRef.current.cancel();
      }

      const oldParams = field.searchOptions.map(searchOption => searchOption.name);

      // Clear all search option fields and their URL parameters
      oldParams.forEach(fieldName => {
        methods.setValue(fieldName as Path<T>, '' as any);
      });

      // Remove all search option URL parameters
      removeParam(field.searchOptions.filter(item => item.name !== option.name).map(searchOption => searchOption.name));
      // Update selected search option state
      setSelectedSearchOption(option);
    }
    // field.onChange?.('');
  };

  const commonProps = {
    name: field.name,
    label: showLabels ? (field.label ? field.label : field.placeholder) : undefined,
    placeholder: field.placeholder,
    disabled: isDisabled,
    required: field.required,
    multiple: field.multiple,
    allowSearch: field.allowSearch,
    loading: field.loading,
    onChange: field.onChange as any,
    onChangeCallback: field.onChangeCallback as any,
    onSearch: field.onSearch,
    validate: field.validate,
    prefix: field.prefix,
    suffix: field.suffix,
    size: field.size || 'small',
    className: field.className,
    rounded: field.rounded,
    mode: field.mode,
  };

  // Handle hidden prop - unconditionally hide the field
  if (field.hidden) {
    return null;
  }

  // Handle hiddenIfEmpty prop - hide the field if its value is empty
  if (field.hiddenIfEmpty) {
    const fieldValue = methods.watch(field.name);
    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || fieldValue === '') {
      return null;
    }
  }

  switch (field.type) {
    case 'search':
      return (
        <TextInput
          key={selectedSearchOption?.name || commonProps.name}
          {...commonProps}
          name={selectedSearchOption?.name || commonProps.name}
          searchOptions={field.searchOptions}
          selectedSearchOption={selectedSearchOption}
          onSearchOptionChange={handleSearchOptionChange}
          rounded={'large'}
          type="search"
        />
      );
    case 'select':
      return (
        <SelectInput
          key={commonProps.name}
          {...commonProps}
          options={field.options || []}
          apiUrl={field.apiUrl}
          apiParams={field.apiParams}
          renderOption={field.renderOption}
          searchKey={field.searchKey}
          cacheKey={field.cacheKey}
          allowClear={field.allowClear}
          mode={field.mode}
        />
      );
    case 'date-range':
      return (
        <DateRangeInput
          key={commonProps.name}
          name={commonProps.name}
          label={showLabels ? field.label : undefined}
          disabled={isDisabled}
          required={field.required}
          onChange={field.onChange as any}
          onChangeCallback={field.onChangeCallback as any}
          startPlaceholder={field.placeholder?.split(',')[0] || 'Start date'}
          endPlaceholder={field.placeholder?.split(',')[1] || 'End date'}
          rounded={field.rounded}
          className={field.className}
          minDate={field.minDate}
          maxDate={field.maxDate}
        />
      );
    default:
      return null;
  }
};
